import {
  Condition,
  IAnrCondition,
  IExpressionCondition,
  IPropCondition,
  IQuery,
  IQueryOrderBy,
  ITimeIntervalCondition,
  ExpressionLogicType,
  LogicalOperandType,
  OdataQueryOperandType,
  PropertyOperandType,
  TimeIntervalType,
  ConditionType,
  IAdvancedCondition,
  LastDaysInterval,
  CustomInterval,
  LastWeeksInterval,
  LastMonthsInterval,
} from "@cayo/shared/components/GridContainer/types";

export const logicalExpressionRegex = new RegExp(`(#)?${ExpressionLogicType.LogicalExpression}`);
export const propertyExpressionRegex = new RegExp(`(#)?${ExpressionLogicType.PropertyExpression}`);
export const odataQueryExpressionRegex = new RegExp(
  `(#)?${ExpressionLogicType.OdataQueryExpression}`
);

export function isPropertyOperandType(
  el: PropertyOperandType | LogicalOperandType | OdataQueryOperandType
): el is PropertyOperandType {
  return (
    el.objectType === ExpressionLogicType.PropertyExpression ||
    (el.objectType.startsWith("#") &&
      el.objectType.slice(1) === ExpressionLogicType.PropertyExpression)
  );
}

export function isOdataQueryOperandType(
  el: PropertyOperandType | LogicalOperandType | OdataQueryOperandType
): el is OdataQueryOperandType {
  return (
    el.objectType === ExpressionLogicType.OdataQueryExpression ||
    (el.objectType.startsWith("#") &&
      el.objectType.slice(1) === ExpressionLogicType.OdataQueryExpression)
  );
}

export function setCondition(query: IQuery, condition: Condition): IQuery {
  const predicate = makeFilterPredicate(condition);
  const result = {
    ...query,
    conditions: query.conditions.filter(predicate).concat([{ ...condition }]),
  };
  return result;
}

function makeFilterPredicate(inp: Condition) {
  return (c: Condition) => {
    if (c?.kind !== inp?.kind) {
      return true;
    }

    switch (c.kind) {
      case ConditionType.Expression:
        return false;
      case ConditionType.TimeInterval:
      case ConditionType.Property:
        return c.propName !== (inp as ITimeIntervalCondition).propName;
      default:
        return false;
    }
  };
}

export function setOrderBy(query: IQuery, order: IQueryOrderBy | undefined): IQuery {
  return { ...query, orderBy: order };
}

export function assignQuery(query: IQuery, props: Partial<IQuery>): IQuery {
  return { ...query, ...props };
}

export function newQuery(): IQuery {
  return { isSystem: false, conditions: [] };
}

export function removeCondition(query: IQuery, condition: Condition): IQuery {
  return { ...query, conditions: query.conditions.filter(makeFilterPredicate(condition)) };
}

// TODO: deep object comparing for expressions
export function isConditionsEqual(
  a: Readonly<Array<Condition>>,
  b: Readonly<Array<Condition>>
): boolean {
  //remove empty conditions
  const one = a.filter((c) => !isEmptyCondition(c));
  const two = b.filter((c) => !isEmptyCondition(c));
  if (one.length !== two.length) {
    return false;
  }
  while (one.length) {
    const oneItem = one.shift();
    const twoItem = two.find((item) => {
      if (item.kind === oneItem?.kind) {
        if (item.kind === ConditionType.Property) {
          return item.propName === (oneItem as IPropCondition).propName;
        }
        return true;
      }
      return false;
    });
    if (!twoItem) {
      return false;
    }
    switch (oneItem?.kind) {
      case ConditionType.Anr:
        if (!anrEquals(oneItem, twoItem as IAnrCondition)) {
          return false;
        }
        break;
      case ConditionType.Expression:
        if (!expressionEquals(oneItem, twoItem as IExpressionCondition)) {
          return false;
        }
        break;
      case ConditionType.Advanced:
        if (!advancedEquals(oneItem, twoItem as IAdvancedCondition)) {
          return false;
        }
        break;
      case ConditionType.TimeInterval:
        if (!timeIntervalsEqual(oneItem, twoItem as ITimeIntervalCondition)) {
          return false;
        }
        break;
      case ConditionType.Property:
        if (!propertyConditionEquals(oneItem, twoItem as IPropCondition)) {
          return false;
        }
        break;
    }
  }
  return true;
}

function expressionEquals(a: IExpressionCondition, b: IExpressionCondition): boolean {
  return JSON.stringify(a.operands) === JSON.stringify(b.operands);
}

function anrEquals(a: IAnrCondition, b: IAnrCondition): boolean {
  return a.value === b.value;
}

function advancedEquals(a: IAdvancedCondition, b: IAdvancedCondition): boolean {
  return a.value === b.value;
}

function timeIntervalsEqual(a: ITimeIntervalCondition, b: ITimeIntervalCondition): boolean {
  if (a.propName !== b.propName) {
    return false;
  }
  if (a.value.objectType !== b.value.objectType) {
    return false;
  }
  switch (a.value.objectType) {
    case TimeIntervalType.LastDays:
      return a.value.number === (b.value as LastDaysInterval).number;
    case TimeIntervalType.Custom:
      return (
        a.value.endDateTime === (b.value as CustomInterval).endDateTime &&
        a.value.startDateTime === (b.value as CustomInterval).startDateTime
      );
    case TimeIntervalType.LastWeeks:
      return a.value.number === (b.value as LastWeeksInterval).number;
    case TimeIntervalType.LastMonths:
      return a.value.number === (b.value as LastMonthsInterval).number;

    default:
      return true;
  }
}

function propertyConditionEquals(a: IPropCondition, b: IPropCondition): boolean {
  if (a.propName !== b.propName) {
    return false;
  }
  return JSON.stringify(a.value) === JSON.stringify(b.value);
}

function isEmptyCondition(condition: Condition): boolean {
  switch (condition.kind) {
    case ConditionType.Expression:
      return condition.operands.length === 0;
    case ConditionType.Advanced:
    case ConditionType.Anr:
    case ConditionType.MaxResults:
      return !condition.value;
    case ConditionType.ModernFilter:
      return !Object.keys(condition.value)?.length;
    case ConditionType.Property:
      return condition.value.length === 0;
    case ConditionType.TimeInterval:
      return false;
  }
}

export function filterDisabledOperandsFromExpression(
  conditionElements: Array<LogicalOperandType | PropertyOperandType | OdataQueryOperandType>
): Array<LogicalOperandType | PropertyOperandType | OdataQueryOperandType> {
  return conditionElements?.filter((c) => {
    if (
      c.objectType === ExpressionLogicType.PropertyExpression ||
      propertyExpressionRegex.test(c.objectType) ||
      c.objectType === ExpressionLogicType.OdataQueryExpression ||
      odataQueryExpressionRegex.test(c.objectType)
    ) {
      return c.enabled;
    } else {
      return c.enabled ? filterDisabledOperandsFromExpression(c.operands) : false;
    }
  });
}
